// LAYOUT-SPEC §0 — the submit drawer (wireframe 2)
//
//   ONE QUESTION  What went out, for which date, and when?
//   PRIMARY OBJECT  One submission (Archetype C).
//   PRIMARY VERB    Submit. The date is always picked: there is no "not sure
//                 which one" state (a one-off date is created inline instead).
//   WORST-CASE VOLUME  A deliverable with a few dozen dates; a few files.
//
// Opened from a date (?submit=<occId>): the date is pre-picked, still
// changeable. Opened from a team or the list (?submit=new): the date must be
// picked before Submit enables. Files upload to tesoro on Submit, then one
// CreateSubmission carries every file and link. A date still waiting on its
// event (a projected row, ?submit=projected:…&forDuty=…) is filed early:
// CreateSubmission stores it, and the drawer then opens the stored date
// (SAD-869).
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraAttachmentRow, SpecraButton, SpecraCheckboxField, SpecraCombobox, SpecraDateField, SpecraFormField, SpecraInput, SpecraSelect, SpecraTag, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FileText, Link2 } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { useAuth } from '../../../../contexts/authContext';
import { useUploadArtifacts } from '../../../../hooks/useDeliveryDocuments';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, useAddOccurrence, useCreateSubmission, useOccurrence, useOccurrences } from '../hooks';
import { cmDateShort, cmMonth, todayISO } from '../lib/format';
import { isOpenState, occurrenceStatus } from '../lib/status';
import { useCmLookups } from '../lookups';
import { CmDrawer } from './CmDrawer';
const NEW_DATE = '__new';
const ME = '__me';
/** A folder link is a soft note, never a block ("This is a folder, not a file"). */ export function isFolderLink(url) {
    return /sharepoint\.com\/:f:\//i.test(url) || /\/Forms\/AllItems\.aspx/i.test(url);
}
function isWebAddress(url) {
    return /^https?:\/\/[^/\s]+/i.test(url.trim());
}
function linkName(url) {
    try {
        const u = new URL(url);
        const last = decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() ?? '');
        return last || u.hostname;
    } catch  {
        return url;
    }
}
/** isCopyOf: o is another team's date of the same rule, period and cycle as of. */ export function isCopyOf(o, of) {
    return o.id !== of.id && o.workstreamId !== of.workstreamId && o.ruleId === of.ruleId && o.nominalDueOn === of.nominalDueOn && o.label === of.label && o.periodOfPerformanceId === of.periodOfPerformanceId && o.exerciseId === of.exerciseId;
}
function dateOptionLabel(o, finalTier, teamName) {
    const s = occurrenceStatus(o, finalTier);
    const period = o.label || cmMonth(o.nominalDueOn || o.dueOn);
    const team = o.workstreamId ? ` · ${teamName(o.workstreamId)}` : '';
    return `${period} · due ${cmDateShort(o.dueOn, 'not stated')}${team} · ${s.label}`;
}
export function SubmitDrawer({ programId, target }) {
    const params = useCmDrawerParams();
    const { user } = useAuth();
    const lk = useCmLookups(programId);
    const projected = target.startsWith('projected:');
    const pre = useOccurrence(programId, target !== 'new' && !projected ? target : undefined);
    const preOcc = pre.data?.occurrence;
    // Picked ids only; every record below is derived from the queries.
    const [dutyPick, setDutyPick] = useState(null);
    const [datePick, setDatePick] = useState(null);
    const dutyId = dutyPick ?? preOcc?.dutyId ?? params.forDuty ?? '';
    const dateId = datePick ?? (dutyPick === null ? preOcc?.id ?? (projected ? target : '') : '');
    // Projected dates too: one waiting on its event can be filed early.
    const dates = useOccurrences(programId, {
        dutyId,
        window: OccurrenceWindow.ALL,
        includeProjected: true
    }, {
        enabled: !!dutyId
    });
    const duty = lk.dutyById.get(dutyId);
    const finalTier = duty?.finalReviewTier ?? 'va_pm';
    const dateRows = useMemo(()=>{
        const rows = (dates.data?.occurrences ?? []).filter((o)=>!o.retired);
        const open = rows.filter((o)=>isOpenState(o.status?.state ?? ''));
        const rest = rows.filter((o)=>!isOpenState(o.status?.state ?? ''));
        const byDue = (a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9');
        return [
            ...open.sort(byDue),
            ...rest.sort(byDue)
        ];
    }, [
        dates.data
    ]);
    const picked = dateRows.find((o)=>o.id === dateId) ?? (preOcc?.id === dateId ? preOcc : undefined);
    const pickedStatus = picked?.status;
    const resubmitAttempt = pickedStatus?.state === 'rejected' ? (pickedStatus.attempt || 1) + 1 : 0;
    const [oneOffDate, setOneOffDate] = useState('');
    const [oneOffLabel, setOneOffLabel] = useState('');
    const [files, setFiles] = useState([]);
    const [links, setLinks] = useState([]);
    const [linkDraft, setLinkDraft] = useState('');
    const dutyPickerId = useId();
    const linkInputId = useId();
    const [submittedOn, setSubmittedOn] = useState(todayISO());
    const [submittedBy, setSubmittedBy] = useState(ME);
    const [sentTo, setSentTo] = useState([]);
    const [alsoOn, setAlsoOn] = useState(false);
    const [also, setAlso] = useState([]);
    // The same filing cycle other teams owe (a platform deliverable's per-team
    // copies, M5): one package answers them all.
    const copies = picked?.ruleId ? dateRows.filter((o)=>isCopyOf(o, picked)) : [];
    const copiesLeft = copies.filter((o)=>!also.includes(o.id));
    const [error, setError] = useState(null);
    const upload = useUploadArtifacts();
    const add = useAddOccurrence(programId, {
        errors: 'inline'
    });
    const create = useCreateSubmission(programId, {
        errors: 'inline'
    });
    const busy = upload.isPending || add.isPending || create.isPending;
    const isNewDate = dateId === NEW_DATE;
    const hasDate = isNewDate ? oneOffDate !== '' : !!dateId;
    const attachmentCount = files.length + links.length;
    const canSubmit = hasDate && attachmentCount > 0 && !!submittedOn && !busy;
    const close = ()=>params.close('submit', 'forDuty', 'forTeam');
    const addLink = ()=>{
        const url = linkDraft.trim();
        if (!isWebAddress(url)) {
            setError(new Error('A link must be a web address starting with https://'));
            return;
        }
        setError(null);
        if (!links.includes(url)) setLinks([
            ...links,
            url
        ]);
        setLinkDraft('');
    };
    const onSubmit = async ()=>{
        setError(null);
        try {
            let occurrenceId = dateId;
            if (isNewDate) {
                const r = await add.mutateAsync({
                    dutyId,
                    dueOn: oneOffDate,
                    label: oneOffLabel.trim(),
                    workstreamId: params.forTeam
                });
                occurrenceId = r.occurrence?.id ?? '';
                // A retry after a later failure must reuse this date, not add another.
                if (occurrenceId) setDatePick(occurrenceId);
            }
            const uploaded = files.length ? await upload.mutateAsync({
                files
            }) : {
                files: [],
                failed: []
            };
            if (uploaded.failed.length) {
                throw new Error(uploaded.failed.map((f)=>`Could not upload ${f.name}: ${f.error}`).join('\n'));
            }
            const contact = lk.contacts.find((c)=>c.id === submittedBy);
            const created = await create.mutateAsync({
                occurrenceId,
                alsoOccurrenceIds: alsoOn ? also : [],
                submittedOn,
                submittedByContactId: contact?.id ?? '',
                submittedByDisplay: contact ? contact.name : user?.name ?? user?.email ?? '',
                sentToContactIds: sentTo,
                attachments: [
                    ...uploaded.files.map((f)=>({
                            artifactId: f.artifactId,
                            label: f.name
                        })),
                    ...links.map((url)=>({
                            url,
                            label: linkName(url)
                        }))
                ]
            });
            // A projected date was stored by the filing: open the stored one.
            const stored = created.submissions[0]?.occurrenceId || occurrenceId;
            params.swap([
                'submit',
                'forDuty',
                'forTeam'
            ], {
                occ: stored
            });
        } catch (e) {
            setError(e);
        }
    };
    const dutyOptions = useMemo(// A milestone (a meeting) is marked held, never filed (SAD-869).
    ()=>[
            ...lk.duties
        ].filter((d)=>!d.endedOn && !d.eventKind).sort((a, b)=>a.title.localeCompare(b.title)), [
        lk.duties
    ]);
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: /*#__PURE__*/ _jsx("span", {
            "data-testid": Testids.cmSubmit.title,
            children: resubmitAttempt ? `Resubmit (attempt ${resubmitAttempt})` : 'Submit deliverable'
        }),
        onClose: close,
        busy: busy,
        testId: Testids.cmSubmit.drawer,
        footerProps: {
            confirmLabel: 'Submit',
            onConfirm: ()=>void onSubmit(),
            confirmDisabled: !canSubmit,
            loading: busy,
            loadingLabel: upload.isPending ? 'Uploading…' : 'Submitting…',
            onCancel: close,
            cancelProps: {
                disabled: busy
            },
            confirmProps: {
                'data-testid': Testids.cmSubmit.submit
            },
            leading: canSubmit ? undefined : /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                "data-testid": Testids.cmSubmit.hint,
                children: "Pick a date and add at least one file or link."
            })
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "For",
                    required: true,
                    htmlFor: dutyPickerId,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                id: dutyPickerId,
                                "aria-label": "Deliverable",
                                value: dutyId,
                                onChange: (e)=>{
                                    setDutyPick(e.target.value);
                                    setDatePick('');
                                },
                                "data-testid": Testids.cmSubmit.dutyPicker,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Pick the deliverable…"
                                    }),
                                    dutyOptions.map((d)=>/*#__PURE__*/ _jsxs("option", {
                                            value: d.id,
                                            children: [
                                                d.title,
                                                d.pwsRef ? ` · PWS ${d.pwsRef}` : ''
                                            ]
                                        }, d.id))
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "aria-label": "Date",
                                value: dateId,
                                disabled: !dutyId,
                                onChange: (e)=>setDatePick(e.target.value),
                                "data-testid": Testids.cmSubmit.datePicker,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: dutyId ? 'Pick the date…' : 'Pick the deliverable first'
                                    }),
                                    dateRows.map((o)=>/*#__PURE__*/ _jsx("option", {
                                            value: o.id,
                                            children: dateOptionLabel(o, finalTier, lk.teamName)
                                        }, o.id)),
                                    /*#__PURE__*/ _jsx("option", {
                                        disabled: true,
                                        children: "──────────"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: NEW_DATE,
                                        "data-testid": Testids.cmSubmit.addOneOff,
                                        children: "+ Add a one-off date for this deliverable…"
                                    })
                                ]
                            })
                        ]
                    })
                }),
                isNewDate ? /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            picker: "native",
                            label: "One-off date due",
                            required: true,
                            value: oneOffDate,
                            onValueChange: setOneOffDate,
                            "data-testid": Testids.cmSubmit.oneOffDate
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Label",
                            hint: "e.g. “Kickoff briefing”",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: oneOffLabel,
                                onChange: (e)=>setOneOffLabel(e.target.value),
                                "data-testid": Testids.cmSubmit.oneOffLabel
                            })
                        })
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "What did you submit?",
                    required: true,
                    htmlFor: linkInputId,
                    hint: "At least one file or link.",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        "data-testid": Testids.cmSubmit.dropzone,
                        children: [
                            /*#__PURE__*/ _jsx(FileUploader, {
                                files: files,
                                onChange: setFiles,
                                disabled: busy,
                                inputTestid: Testids.cmSubmit.dropzoneInput
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: linkInputId,
                                        "aria-label": "Paste a link",
                                        placeholder: "Paste a link, e.g. https://…sharepoint.com/:b:/r/sites/…",
                                        value: linkDraft,
                                        onChange: (e)=>setLinkDraft(e.target.value),
                                        onKeyDown: (e)=>{
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addLink();
                                            }
                                        },
                                        "data-testid": Testids.cmSubmit.linkInput
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        disabled: !linkDraft.trim(),
                                        onClick: addLink,
                                        "data-testid": Testids.cmSubmit.linkAdd,
                                        children: "Add link"
                                    })
                                ]
                            })
                        ]
                    })
                }),
                links.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-1.5",
                    children: links.map((url)=>/*#__PURE__*/ _jsx("li", {
                            "data-testid": Testids.cmSubmit.attachmentRow,
                            children: /*#__PURE__*/ _jsx(SpecraAttachmentRow, {
                                name: linkName(url),
                                icon: /*#__PURE__*/ _jsx(Link2, {
                                    className: "h-3.5 w-3.5",
                                    "aria-hidden": true
                                }),
                                meta: isFolderLink(url) ? /*#__PURE__*/ _jsx("span", {
                                    className: "text-warning",
                                    "data-testid": Testids.cmSubmit.folderNote,
                                    children: "This is a folder, not a file. It still counts; link the file if you can."
                                }) : url,
                                onRemove: ()=>setLinks(links.filter((l)=>l !== url))
                            })
                        }, url))
                }) : null,
                files.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                    className: "sr-only",
                    children: files.map((f)=>/*#__PURE__*/ _jsxs("li", {
                            "data-testid": Testids.cmSubmit.attachmentRow,
                            children: [
                                /*#__PURE__*/ _jsx(FileText, {
                                    "aria-hidden": true
                                }),
                                " ",
                                f.name
                            ]
                        }, f.name))
                }) : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            picker: "native",
                            label: "Submitted on",
                            required: true,
                            value: submittedOn,
                            onValueChange: setSubmittedOn,
                            hint: "Back-date it if it went out earlier.",
                            "data-testid": Testids.cmSubmit.submittedOn
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Submitted by",
                            children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                                label: "Submitted by",
                                options: [
                                    {
                                        value: ME,
                                        label: user?.name ? `${user.name} (me)` : 'Me'
                                    },
                                    ...lk.contacts.map((c)=>({
                                            value: c.id,
                                            label: c.name
                                        }))
                                ],
                                value: submittedBy,
                                onValueChange: (v)=>setSubmittedBy(v ?? ME),
                                searchPlaceholder: "Search people…",
                                triggerProps: {
                                    'data-testid': Testids.cmSubmit.submittedBy
                                }
                            })
                        })
                    ]
                }),
                lk.contacts.length > 0 ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Sent to",
                    hint: "Optional: who received it.",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1.5",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraCombobox, {
                                label: "Sent to",
                                options: lk.contacts.filter((c)=>!sentTo.includes(c.id)).map((c)=>({
                                        value: c.id,
                                        label: c.name
                                    })),
                                // A multi-pick: each pick adds a recipient and the box resets.
                                value: "",
                                onValueChange: (v)=>{
                                    if (v && !sentTo.includes(v)) setSentTo([
                                        ...sentTo,
                                        v
                                    ]);
                                },
                                placeholder: "Add a recipient…",
                                searchPlaceholder: "Search contacts…",
                                triggerProps: {
                                    'data-testid': Testids.cmSubmit.sentTo
                                }
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-wrap gap-1.5",
                                children: sentTo.map((id)=>/*#__PURE__*/ _jsx(SpecraTag, {
                                        onRemove: ()=>setSentTo(sentTo.filter((x)=>x !== id)),
                                        removeLabel: `Remove ${lk.contacts.find((c)=>c.id === id)?.name ?? 'recipient'}`,
                                        children: lk.contacts.find((c)=>c.id === id)?.name ?? 'Contact'
                                    }, id))
                            })
                        ]
                    })
                }) : null,
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        copiesLeft.length > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            onClick: ()=>{
                                setAlsoOn(true);
                                setAlso([
                                    ...also,
                                    ...copiesLeft.map((o)=>o.id)
                                ]);
                            },
                            "data-testid": Testids.cmSubmit.allCopies,
                            children: `File for all ${copies.length + 1} teams' copies of this date`
                        }) : null,
                        /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                            label: "Also counts for another date…",
                            description: "One package answering two dates: each gets its own submission sharing these files.",
                            controlProps: {
                                checked: alsoOn,
                                onCheckedChange: (v)=>setAlsoOn(v === true),
                                'data-testid': Testids.cmSubmit.alsoCounts
                            }
                        }),
                        alsoOn ? /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: "",
                            onChange: (e)=>{
                                const v = e.target.value;
                                if (v && !also.includes(v)) setAlso([
                                    ...also,
                                    v
                                ]);
                            },
                            "data-testid": Testids.cmSubmit.alsoPicker,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Add a date…"
                                }),
                                dateRows.filter((o)=>o.id !== dateId && !also.includes(o.id)).map((o)=>/*#__PURE__*/ _jsx("option", {
                                        value: o.id,
                                        children: dateOptionLabel(o, finalTier, lk.teamName)
                                    }, o.id))
                            ]
                        }) : null,
                        alsoOn && also.length > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            size: "caption",
                            tone: "muted",
                            children: [
                                "Also for:",
                                ' ',
                                also.map((id)=>dateRows.find((o)=>o.id === id)).filter(Boolean).map((o)=>dateOptionLabel(o, finalTier, lk.teamName)).join('; ')
                            ]
                        }) : null
                    ]
                }),
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    "data-testid": Testids.cmSubmit.error,
                    children: apiErrorMessage(error, 'Could not submit')
                }) : null
            ]
        })
    });
}
